import React, { useEffect, useState } from 'react';
import { NextGenImage } from '../components/OptimizedImage';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Eye, User, Tag, Crown, Share2, ArrowLeft, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { SkeletonArticle } from '../components/LoadingSpinner';
import PremiumContentGate from '../components/PremiumContentGate';
import MagazineReader from '../components/MagazineReader';
import { useAuth } from '../context/AuthContext';
import { useArticle, useArticles } from '../hooks/useArticles';
import { formatDate, formatReadingTime } from '../utils/formatters';
import DOMPurify from 'dompurify';

// Removes all <script> tags for security
const cleanHTML = (html) => {
  const noScripts = html.replace(/<script.*?>.*?<\/script>/gi, '');
  return DOMPurify.sanitize(noScripts, { USE_PROFILES: { html: true } });
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { data: article, isLoading, error } = useArticle(slug);
  const { data: allArticles } = useArticles();
  const [isReaderOpen, setIsReaderOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // fallback Atlantis article (same as before, shortened here for brevity)
  const atlantisArticle = {
    id: 'atlantis-the-palm-dubai',
    slug: 'atlantis-the-palm-dubai',
    title: "Atlantis The Palm: A Mythical Journey to Dubai's Crown Jewel",
    subtitle: "This month we head to Atlantis, the Palm in Dubai...",
    category: "Travel",
    subcategory: "Luxury Stays",
    author: { name: "Chahat Dalal" },
    publishDate: "2022-07-01T00:00:00Z",
    readingTime: 8,
    heroImage: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=1920&h=1080&fit=crop",
    isPremium: false,
    tags: ["Dubai", "Luxury Travel", "Atlantis"],
    content: `<p class="lead">Atlantis - the lost island...</p>`
  };

  const displayArticle = article || (slug === "atlantis-the-palm-dubai" ? atlantisArticle : null);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-12">
          <SkeletonArticle />
        </div>
      </div>
    );
  }

  if (!displayArticle || (error && slug !== "atlantis-the-palm-dubai")) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The article you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate("/")}
            className="btn-primary"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Normalize DB `is_premium` (01 or true/false or string/number)
  const isPremiumFromDB =
    displayArticle.is_premium === 1 ||
    displayArticle.is_premium === "1" ||
    displayArticle.isPremium;

  // Determine if user can read premium
  const canReadPremium =
    isAuthenticated && user?.is_premium && user?.subscription_status === "active";

  // Only lock the article if it's premium and user cannot read
  const isLocked = isPremiumFromDB && !canReadPremium;

  // Use original category label logic
  const categoryLabels = {
    fashion: "Look Good",
    technology: "Get Smart",
    tech: "Get Smart",
    business: "Get Smart",
    finance: "Get Smart",
    travel: "Live Well",
    health: "Live Well",
    culture: "Entertainment",
    art: "Entertainment",
    entertainment: "Entertainment",
    auto: "Live Well",
    grooming: "Look Good",
    food: "Live Well",
    aviation: "Live Well",
    people: "Entertainment",
    luxury: "Live Well"
  };

  const categoryLabel =
    categoryLabels[displayArticle?.category?.toLowerCase()] || "Category";

  const openMagazineReader = () => {
    if (allArticles && allArticles.length > 0) {
      const currentIndex = allArticles.findIndex(
        (a) => a.slug === displayArticle.slug || a.id === displayArticle.id
      );
      const magazineArticles =
        currentIndex >= 0
          ? [...allArticles.slice(currentIndex), ...allArticles.slice(0, currentIndex)]
          : allArticles;
      setIsReaderOpen(true);
    }
  };

  const closeMagazineReader = () => setIsReaderOpen(false);

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: displayArticle.title,
        text: displayArticle.subtitle || displayArticle.dek,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900 font-medium">Home</Link>
            <span>/</span>
            <Link
              to={`/category/${displayArticle.category}`}
              className="hover:text-gray-900 font-medium capitalize"
            >
              {displayArticle.category}
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-bold">{displayArticle.title}</span>
          </nav>
        </div>
      </div>

      <article className="container mx-auto px-4 py-16">
        {/* Header */}
        <motion.div
          className="max-w-4xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-6">
            <span className="text-sm font-bold text-gray-600 uppercase tracking-widest">
              {categoryLabel}
            </span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            {displayArticle.title}
          </h1>

          {(displayArticle.dek || displayArticle.subtitle) && (
            <p className="text-xl md:text-2xl text-gray-600 mb-8 font-light">
              {displayArticle.dek || displayArticle.subtitle}
            </p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 py-6 border-t border-b border-gray-200 text-sm text-gray-500">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              <span>By {displayArticle.author_name || displayArticle.author?.name || displayArticle.author}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              <time>{displayArticle.publishDate ? formatDate(displayArticle.publishDate) : displayArticle.date}</time>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              <span>{displayArticle.readingTime ? `${displayArticle.readingTime} min read` : formatReadingTime(displayArticle.reading_time)}</span>
            </div>
            {displayArticle.view_count > 0 && (
              <div className="flex items-center">
                <Eye className="h-4 w-4 mr-2" />
                <span>{displayArticle.view_count.toLocaleString()} views</span>
              </div>
            )}
            <button
              onClick={shareArticle}
              className="flex items-center hover:text-gray-700 ml-auto"
            >
              <Share2 className="h-4 w-4 mr-2" /> Share
            </button>
          </div>

          {allArticles && allArticles.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <button
                onClick={openMagazineReader}
                className="inline-flex items-center bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition-all"
              >
                <BookOpen className="h-5 w-5 mr-2" />
                Read in Magazine Mode <Crown className="h-4 w-4 ml-2" />
              </button>
            </div>
          )}
        </motion.div>

        {/* Hero Image */}
        {(displayArticle.hero_image || displayArticle.heroImage) && (
          <motion.div
            className="mb-12 max-w-6xl mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={displayArticle.hero_image || displayArticle.heroImage}
                alt={displayArticle.title}
                className="w-full h-96 md:h-[600px] object-fill"
                onError={(e) => (e.target.src = "/placeholder-article.jpg")}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </motion.div>
        )}

        <div className="max-w-4xl mx-auto">
          <motion.div
            className="prose prose-lg lg:prose-xl max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Show premium gate ONLY if isPremiumFromDB and isLocked */}
            {isPremiumFromDB && isLocked ? (
              <PremiumContentGate article={displayArticle} showPreview={true} />
            ) : (
              <div
                className="article-content prose prose-lg lg:prose-xl max-w-none"
                dangerouslySetInnerHTML={{
                  __html: cleanHTML(displayArticle.content || displayArticle.body || ""),
                }}
              />
            )}
          </motion.div>

          {/* Tags */}
          {displayArticle.tags && displayArticle.tags.length > 0 && !isLocked && (
            <motion.div
              className="mt-12 pt-8 border-t border-gray-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="flex items-center mb-4">
                <Tag className="h-5 w-5 text-gray-600 mr-2" />
                <span className="font-medium text-gray-900">Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {displayArticle.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="bg-gray-100 hover:bg-primary-100 text-gray-700 hover:text-primary-700 px-3 py-1 rounded-full text-sm"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            className="mt-16 pt-8 border-t border-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link
              to={`/category/${displayArticle.category}`}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {displayArticle.category} Articles
            </Link>
          </motion.div>
        </div>
      </article>

      {/* Magazine Reader */}
      <MagazineReader
        articles={allArticles}
        isOpen={isReaderOpen}
        onClose={closeMagazineReader}
        initialPageIndex={
          allArticles
            ? allArticles.findIndex(
                (a) => a.slug === displayArticle.slug || a.id === displayArticle.id
              ) * 2 + 1
            : 0
        }
      />
    </div>
  );
};

export default ArticlePage;
